import { HQDisplayGroup, PaginationParams, HQDisplayGroupFilters, SubClass } from '../types/hqDisplayGroup';
import { Item } from '../types/item';
import { ApiResponse, PaginatedResponse, } from '../types/index';
import { mockHQDisplayGroups, mockSubClasses, mockItems, mockDivisions, mockCategories } from '../mock/hqDisplayGroupData';
// 修改引用路径
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import { createSuccessResponse, createErrorResponse, createPaginatedResponse } from '../utils/responseTransformer';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';
import { itemService } from './itemService';

/**
 * Service class for HQ Display Group operations
 */
export class HQDisplayGroupService {
  private static instance: HQDisplayGroupService;
  private displayGroups: HQDisplayGroup[] = [...mockHQDisplayGroups];
  
  // 静态配置，控制是否使用 mock 数据
  private static useMock: boolean = false;

  /**
   * Get singleton instance
   */
  public static getInstance(): HQDisplayGroupService {
    if (!HQDisplayGroupService.instance) {
      HQDisplayGroupService.instance = new HQDisplayGroupService();
    }
    return HQDisplayGroupService.instance;
  }
  
  /**
   * 设置是否使用 mock 数据
   * @param useMock 是否使用 mock 数据
   */
  public static setUseMock(useMock: boolean): void {
    HQDisplayGroupService.useMock = useMock;
  }
  
  /**
   * 获取当前是否使用 mock 数据
   */
  public static getUseMock(): boolean {
    return HQDisplayGroupService.useMock;
  }

  /**
   * Simulate API delay
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get HQ Display Groups with filtering, pagination and sorting
   */
  public async getHQDisplayGroups(
    filters: HQDisplayGroupFilters = {},
    pagination: PaginationParams = { page: 1, pageSize: 10 }
  ): Promise<ApiResponse<PaginatedResponse<HQDisplayGroup>>> {
    try {
      // 根据配置决定使用 mock 数据还是调用真实 API
      if (HQDisplayGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        // Apply filters
        let filteredGroups = [...this.displayGroups];

        // Calculate hasWhitelistItems and hasBlacklistItems for each group
        filteredGroups = filteredGroups.map(group => ({
          ...group,
          hasWhitelistItems: (group.whiteListItems && group.whiteListItems.length > 0) ? 'YES' : 'NO',
          hasBlacklistItems: (group.blackListItems && group.blackListItems.length > 0) ? 'YES' : 'NO'
        }));

        // Filter by status
        if (filters.status && filters.status !== 'all') {
          filteredGroups = filteredGroups.filter(group => group.status === filters.status);
        }

        // Filter by category
        if (filters.category) {
          filteredGroups = filteredGroups.filter(group =>
            group.subClassSets.some(cat => cat.subClassName === filters.category || cat.className === filters.category)
          );
        }

        // Filter by main division
        if (filters.mainDivision) {
          filteredGroups = filteredGroups.filter(group => group.mainDivision === filters.mainDivision);
        }

        // Filter by hasWhitelistItems
        if (filters.hasWhitelistItems && filters.hasWhitelistItems !== 'all') {
          filteredGroups = filteredGroups.filter(group => group.hasWhitelistItems === filters.hasWhitelistItems);
        }

        // Filter by hasBlacklistItems
        if (filters.hasBlacklistItems && filters.hasBlacklistItems !== 'all') {
          filteredGroups = filteredGroups.filter(group => group.hasBlacklistItems === filters.hasBlacklistItems);
        }

        // Filter by search term (code or name)
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          filteredGroups = filteredGroups.filter(
            group =>
              group.code.toLowerCase().includes(searchTerm) ||
              group.name.toLowerCase().includes(searchTerm)
          );
        }

        // Apply sorting
        if (pagination.sortBy) {
          filteredGroups.sort((a, b) => {
            const sortField = pagination.sortBy as keyof HQDisplayGroup;
            let valueA: any = sortField ? a[sortField] : null;
            let valueB: any = sortField ? b[sortField] : null;

            // Handle date sorting
            if (sortField === 'gmtCreate' || sortField === 'gmtModified' || sortField === 'lastSyncTime' || sortField === 'createTime' || sortField === 'updateTime') {
              // Support both field name variants
              const actualFieldA = sortField === 'createTime' ? 'gmtCreate' : sortField === 'updateTime' ? 'gmtModified' : sortField;
              const actualFieldB = actualFieldA;
              valueA = a[actualFieldA] ? new Date(a[actualFieldA]).getTime() : 0;
              valueB = b[actualFieldB] ? new Date(b[actualFieldB]).getTime() : 0;
            }

            // Handle YES/NO sorting for whitelist and blacklist fields
            if (sortField === 'hasWhitelistItems' || sortField === 'hasBlacklistItems') {
              valueA = valueA === 'YES' ? 1 : 0;
              valueB = valueB === 'YES' ? 1 : 0;
            }

            // Apply sort order
            if (pagination.sortOrder === 'asc') {
              return valueA > valueB ? 1 : -1;
            } else {
              return valueA < valueB ? 1 : -1;
            }
          });
        } else {
          // Default sort by gmtCreate desc
          filteredGroups.sort((a, b) => {
            return new Date(b.gmtCreate).getTime() - new Date(a.gmtCreate).getTime();
          });
        }

        // Apply pagination
        const total = filteredGroups.length;
        const startIndex = (pagination.page - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const paginatedGroups = filteredGroups.slice(startIndex, endIndex);

        return createPaginatedResponse(paginatedGroups, pagination.page, pagination.pageSize, total);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.HQ_DISPLAY_GROUPS.PAGE, { ...filters, ...pagination, currentPage: pagination.page });
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching HQ display groups:', error);
      return createErrorResponse(error.message || '获取总部门店组组失败');
    }
  }

  /**
   * Get HQ Display Group by code
   */
  public async getHQDisplayGroupByCode(code: string): Promise<ApiResponse<HQDisplayGroup>> {
    if (HQDisplayGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const displayGroup = this.displayGroups.find(group => group.code === code);

        if (!displayGroup) {
          return createErrorResponse('总部陈列分组不存在', 'NOT_FOUND');
        }

        return createSuccessResponse(displayGroup);
      } else {
        // 调用真实 API
        const response = await httpClient.get(API_ENDPOINTS.HQ_DISPLAY_GROUPS.QUERY_BY_CODE + "?code=" + code);
        return response.data;
      }
  }

  /**
   * Create new HQ Display Group
   */
  public async createHQDisplayGroup(displayGroup: HQDisplayGroup): Promise<ApiResponse<HQDisplayGroup>> {
    if (HQDisplayGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        // Check if code already exists
        const existingGroup = this.displayGroups.find(group => group.code === displayGroup.code);
        if (existingGroup) {
          return createErrorResponse('陈列分组编码已存在', 'DUPLICATE_CODE');
        }

        // Set creation and update times
        const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
        const newDisplayGroup: HQDisplayGroup = {
          ...displayGroup,
          gmtCreate: now,
          gmtModified: now,
          associatedStoreGroups: 0,
        };

        // Add to collection
        this.displayGroups.push(newDisplayGroup);

        return createSuccessResponse(newDisplayGroup);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.HQ_DISPLAY_GROUPS.CREATE, displayGroup);
        return response.data;
      }
  }

  /**
   * Update existing HQ Display Group
   */
  public async updateHQDisplayGroup(code: string, displayGroup: Partial<HQDisplayGroup>): Promise<ApiResponse<HQDisplayGroup>> {
    if (HQDisplayGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const index = this.displayGroups.findIndex(group => group.code === code);
        if (index === -1) {
          return createErrorResponse('总部陈列分组不存在', 'NOT_FOUND');
        }

        // Check if trying to change status to DISABLE while having associated store groups
        if (displayGroup.status === 'DISABLE' && this.displayGroups[index].associatedStoreGroups && this.displayGroups[index].associatedStoreGroups! > 0) {
          return createErrorResponse('该陈列分组正在被门店组使用，无法停用', 'IN_USE');
        }

        // Update the display group
        const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
        const updatedDisplayGroup: HQDisplayGroup = {
          ...this.displayGroups[index],
          ...displayGroup,
          code, // Ensure code doesn't change
          gmtModified: now,
        };

        this.displayGroups[index] = updatedDisplayGroup;

        return createSuccessResponse(updatedDisplayGroup);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.HQ_DISPLAY_GROUPS.UPDATE, displayGroup);
        return response.data;
      }
  }

  /**
   * Batch update status (activate/deactivate)
   */
  public async batchUpdateStatus(ids: number[], status: 'ENABLE' | 'DISABLE'): Promise<ApiResponse<{ success: number, skipped: number }>> {
    if (HQDisplayGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        let successCount = 0;
        let skippedCount = 0;

        for (const id of ids) {
          const index = this.displayGroups.findIndex(group => group.id === id);
          if (index === -1) {
            skippedCount++;
            continue;
          }

          // Update status and update time
          const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
          this.displayGroups[index] = {
            ...this.displayGroups[index],
            status,
            gmtModified: now,
          };

          successCount++;
        }

        return createSuccessResponse({ success: successCount, skipped: skippedCount });
      } else {
        // 调用真实 API
        if (status === 'DISABLE') {
          const response = await httpClient.post(API_ENDPOINTS.HQ_DISPLAY_GROUPS.BATCH_DEACTIVE, {
            ids,
            status
          });
          return response.data;
        } else {
          const response = await httpClient.post(API_ENDPOINTS.HQ_DISPLAY_GROUPS.BATCH_ENABLE, {
            ids,
            status
          });
          return response.data;
        }
      }
  }

  /**
   * Get available divisions for 主要事业部 selection
   */
  public async getDisplayGroups(query): Promise<ApiResponse<HQDisplayGroup[]>> {
    if (HQDisplayGroupService.useMock && WHOLE_USE_MOCK) {
        let filteredGroups = [...this.displayGroups];
        return createSuccessResponse(filteredGroups);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.HQ_DISPLAY_GROUPS.MULTI_QUERY, query);
        return response.data;
      }
  }
}

// Export singleton instance
export default HQDisplayGroupService.getInstance();